import { useEffect } from "react";
import { Link } from "react-router-dom";
import { WardenMark } from "../brand/marks";
import { WARDEN, WARDEN_SERVICES } from "../nav/navigation";
import { SERVICE_PAGES, WARDEN_PRACTICES } from "../content";
import type { CtaLink, WardenHubContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * Warden hub. The practice listing is generated from `WARDEN_SERVICES` so the
 * page and the mega-menu cannot disagree about which services exist, and each
 * entry's one-liner comes from the service page itself rather than a second
 * description that would drift.
 */
export function WardenPage({ content }: { content: WardenHubContent }) {
  useEffect(() => {
    document.title = "Warden — Governed Autonomy by Odingard";
  }, []);

  return (
    <main className="page pdp warden" id="main">
      <section className="pdp__hero warden__hero">
        <span className="warden__lockup">
          <WardenMark size={44} />
          <span>
            <b>WARDEN</b>
            <small>{WARDEN.byline}</small>
          </span>
        </span>
        <p className="page__eyebrow">{WARDEN.heading}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
        <div className="pdp__ctas">
          {content.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>

      <section className="pdp__section" id="premise">
        <h2>{content.premise.heading}</h2>
        <p className="pdp__body">{content.premise.body}</p>
        <ol className="pdp__steps">
          {content.premise.points.map((point) => (
            <li key={point.title}>
              <b>{point.title}</b>
              <span>{point.body}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="pdp__section" id="practices">
        <h2>The five practices</h2>
        <div className="warden__practices">
          {WARDEN_SERVICES.map((group) => (
            <article key={group.title}>
              <h3>{group.title}</h3>
              {WARDEN_PRACTICES[group.title] && <p>{WARDEN_PRACTICES[group.title]}</p>}
              <ul>
                {group.links.map((link) => {
                  const page = SERVICE_PAGES.find((p) => p.path === link.href);
                  return (
                    <li key={link.href}>
                      <Link to={link.href}>{link.label} →</Link>
                      {page && <span>{page.summary}</span>}
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="pdp__section" id="method">
        <h2>{content.method.heading}</h2>
        <p className="pdp__body">{content.method.lede}</p>
        <div className="pdp__features">
          {content.method.steps.map((step, i) => (
            <article key={step.title}>
              <span className="service__step">{i + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
